import React, { useEffect, useState } from "react";
import { apiPost } from "../api";
import serverName from "../serverName";
import { motion } from "framer-motion";
import { useAuth } from "../AuthContext";

const WL_ROLE_ID = "1325494909775646888";

export default function Whitelist() {
    const { me, loading, login, logout } = useAuth();
    const [form, setForm] = useState({ nick: "", age: "", rpExp: "", whyUs: "", rulesAck: false });
    const [sent, setSent] = useState(null);
    const [err, setErr] = useState(null);

    const hasWhitelist =
        me &&
        Array.isArray(me.discord_roles) &&
        me.discord_roles.includes(WL_ROLE_ID);

    useEffect(() => {
        if (me) {
            setForm(f => ({
                ...f,
                nick: f.nick || me.display || me.username || ""
            }));
        }
    }, [me]);

    function onChange(e) {
        const { name, value, type, checked } = e.target;
        setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    }

    async function onSubmit(e) {
        e.preventDefault();
        setErr(null);
        const payload = {
            nick: form.nick.trim(),
            age: form.age.trim(),
            rpExp: form.rpExp.trim(),
            whyUs: form.whyUs.trim(),
            rulesAck: !!form.rulesAck
        };
        const r = await apiPost("/whitelist_submit.php", payload);
        if (r.ok) setSent(r);
        else setErr(r.error || "Chyba");
    }

    if (loading) {
        return (
            <div className="w-full flex flex-col items-center justify-center py-32 gap-4 text-white/70">
                <div className="h-10 w-10 border-4 border-white/20 border-t-white rounded-full animate-spin" />
                <div className="text-lg tracking-wide">Načítám…</div>
            </div>
        );
    }

    if (!me) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-20 text-center">
                <motion.h1
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center text-5xl font-extrabold mb-14 bg-gradient-to-b from-white to-white/50 bg-clip-text text-transparent tracking-wide"
                >
                    Whitelist žádost
                </motion.h1>
                <p className="text-white/70 mb-8">
                    Pro odeslání žádosti se přihlas přes Discord na {serverName}.
                </p>
                <button
                    onClick={login}
                    className="px-5 py-2.5 rounded-xl bg-white text-black text-sm font-semibold hover:opacity-90 flex items-center gap-2 mx-auto"
                >
                    <i className="fox-icon fox-icon-discord"></i>
                    Přihlásit přes Discord
                </button>
            </div>
        );
    }

    if (hasWhitelist) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-20 text-center">
                <motion.h1
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center text-5xl font-extrabold mb-6 bg-gradient-to-b from-white to-white/50 bg-clip-text text-transparent tracking-wide"
                >
                    Už máš whitelist
                </motion.h1>
                <p className="text-white/70">
                    Na tvém Discord účtu je už whitelist role, další žádost není potřeba.
                </p>
                <div className="mt-8 flex items-center justify-center gap-3 text-sm text-white/80">
                    <img src={me.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                    <div className="truncate max-w-[200px]">{me.display || me.username}</div>
                    <button
                        onClick={logout}
                        className="ml-2 px-3 py-1.5 rounded-lg border border-white/15 text-xs hover:bg-white/5"
                    >
                        Odhlásit
                    </button>
                </div>
            </div>
        );
    }

    if (sent) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-20 text-center">
                <motion.h1
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center text-5xl font-extrabold mb-14 bg-gradient-to-b from-white to-white/50 bg-clip-text text-transparent tracking-wide"
                >
                    Žádost odeslána
                </motion.h1>
                <p className="text-white/70">ID žádosti: {sent.request_id}</p>
                <p className="text-white/70 mt-2">Zpráva byla odeslána moderátorům.</p>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto px-4 py-14">
            <motion.h1
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center text-5xl font-extrabold mb-14 bg-gradient-to-b from-white to-white/50 bg-clip-text text-transparent tracking-wide"
            >
                Whitelist žádost
            </motion.h1>
            <div className="mb-6 flex items-center gap-3">
                <img src={me.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                <div className="text-white/90 truncate">{me.display || me.username}</div>
                <button
                    onClick={logout}
                    className="ml-auto px-3 py-1.5 rounded-lg border border-white/15 text-sm hover:bg-white/5"
                >
                    Odhlásit
                </button>
            </div>

            {err && <div className="mb-6 text-sm text-red-400">{err}</div>}
            <form onSubmit={onSubmit} className="space-y-6">
                <div>
                    <label className="block mb-2 text-sm">Jméno</label>
                    <input
                        name="nick"
                        value={form.nick}
                        onChange={onChange}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 outline-none"
                        required
                    />
                </div>
                <div>
                    <label className="block mb-2 text-sm">Věk</label>
                    <input
                        name="age"
                        value={form.age}
                        onChange={onChange}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 outline-none"
                        required
                    />
                </div>
                <div>
                    <label className="block mb-2 text-sm">RP zkušenosti</label>
                    <textarea
                        name="rpExp"
                        value={form.rpExp}
                        onChange={onChange}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 outline-none min-h-[120px]"
                        required
                    />
                </div>
                <div>
                    <label className="block mb-2 text-sm">Proč chceš na Amensia</label>
                    <textarea
                        name="whyUs"
                        value={form.whyUs}
                        onChange={onChange}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 outline-none min-h-[120px]"
                        required
                    />
                </div>
                <label className="flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        name="rulesAck"
                        checked={form.rulesAck}
                        onChange={onChange}
                        className="scale-110"
                        required
                    />
                    Souhlasím s pravidly serveru
                </label>
                <button className="px-5 py-2.5 rounded-xl bg-white text-black text-sm font-semibold hover:opacity-90">
                    Odeslat žádost
                </button>
            </form>
        </div>
    );
}
